import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, Select, MenuItem,
  IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Button, useMediaQuery, Switch, Tooltip, TextField,
  List, ListItemButton, ListItemText, InputAdornment,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CasinoIcon from '@mui/icons-material/Casino';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  getUsers, setUserRole, deleteUser, getPendingStories, reviewStory,
  getAdminGroups, deleteGroup, setTreeEditRights,
  getPendingPersonChanges, reviewPersonChange,
  getPendingSpouseAdditions, reviewSpouseAddition,
  adminSearchPeople, adminCreateAccount,
} from '../api';

const ROLE_COLORS = { ADMIN: 'secondary', PRESIDENT: 'secondary', ELDER: 'primary', MEMBER: 'default' };

const AdminPage = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [toDelete, setToDelete] = useState(null);
  const [pendingStories, setPendingStories] = useState([]);
  const [chatGroups, setChatGroups] = useState([]);
  const [pendingNames, setPendingNames] = useState([]);
  const [pendingSpouses, setPendingSpouses] = useState([]);
  const isMobile = useMediaQuery('(max-width:600px)');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setUsers(await getUsers());
      setPendingStories(await getPendingStories().catch(() => []));
      setChatGroups(await getAdminGroups().catch(() => []));
      setPendingNames(await getPendingPersonChanges().catch(() => []));
      setPendingSpouses(await getPendingSpouseAdditions().catch(() => []));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 4000);
  };

  const handleRoleChange = async (user, role) => {
    setError('');
    try {
      await setUserRole(user._id, role);
      flash(`${user.name} is now ${role}.`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not change role.');
    }
  };

  const handleTreeEditToggle = async (user, canEditTree) => {
    setError('');
    try {
      const data = await setTreeEditRights(user._id, canEditTree);
      flash(data.message);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update tree-editing rights.');
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setError('');
    try {
      const data = await deleteUser(toDelete._id);
      flash(data.message);
      setToDelete(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not delete the account.');
      setToDelete(null);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 950, mx: 'auto', p: { xs: 1.5, sm: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Admin — Family Accounts</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Promote trusted members (ELDER can edit the tree, PRESIDENT has full powers).
        The "Tree editor" switch grants ONE specific person the right to edit and
        delete people in the Members Table — separate from their role — so you can
        designate a single trusted helper without giving them full ELDER/PRESIDENT
        power. Only the admin and whoever holds this switch see the edit/delete
        buttons there. Deleting an account releases its tree node so the rightful
        person can claim it — the person in the tree is never deleted from here.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <AssistRegistrationCard onCreated={load} />

      <Card sx={{ borderRadius: 3, overflowX: 'auto' }}>
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>}
                {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Tree node</TableCell>}
                <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                {!isMobile && <TableCell sx={{ fontWeight: 700 }} align="center">Tree editor</TableCell>}
                <TableCell sx={{ fontWeight: 700 }} align="right">Remove</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => {
                const isSelf = u._id === currentUser?.id || u._id === currentUser?._id;
                return (
                  <TableRow key={u._id} hover>
                    <TableCell>
                      {u.name}
                      {!u.isActive && <Chip label="Deactivated" size="small" sx={{ ml: 1 }} />}
                      {isSelf && <Chip label="You" size="small" color="secondary" sx={{ ml: 1 }} />}
                      {isMobile && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {u.phone || 'no phone'} • {u.person?.name || 'no node'}
                        </Typography>
                      )}
                    </TableCell>
                    {!isMobile && <TableCell>{u.phone || '—'}</TableCell>}
                    {!isMobile && <TableCell>{u.person?.name || '—'}</TableCell>}
                    <TableCell>
                      <Select
                        size="small"
                        value={u.role}
                        disabled={isSelf}
                        onChange={(e) => handleRoleChange(u, e.target.value)}
                        renderValue={(v) => <Chip label={v} size="small" color={ROLE_COLORS[v]} />}
                      >
                        <MenuItem value="MEMBER">MEMBER</MenuItem>
                        <MenuItem value="ELDER">ELDER</MenuItem>
                        <MenuItem value="PRESIDENT">PRESIDENT</MenuItem>
                        <MenuItem value="ADMIN">ADMIN</MenuItem>
                      </Select>
                    </TableCell>
                    {!isMobile && (
                      <TableCell align="center">
                        {u.role === 'ADMIN' ? (
                          <Tooltip title="The admin always has this right">
                            <span><Switch checked disabled size="small" /></span>
                          </Tooltip>
                        ) : (
                          <Tooltip title={u.canEditTree ? 'Can edit/delete people in the tree' : 'Cannot edit/delete people in the tree'}>
                            <Switch
                              size="small"
                              checked={Boolean(u.canEditTree)}
                              onChange={(e) => handleTreeEditToggle(u, e.target.checked)}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <IconButton
                        color="error"
                        disabled={isSelf}
                        onClick={() => setToDelete(u)}
                        aria-label={`Delete account of ${u.name}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pending history stories */}
      <Card sx={{ borderRadius: 3, mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Pending history stories {pendingStories.length > 0 && `(${pendingStories.length})`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Stories submitted by family members. Approved stories appear on the
            Our History timeline for everyone.
          </Typography>
          {pendingStories.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No stories waiting for review.</Typography>
          ) : (
            pendingStories.map((story) => (
              <Card key={story._id} variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
                <CardContent sx={{ pb: '12px !important' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {story.title} <Typography component="span" variant="body2" color="text.secondary">
                      ({new Date(story.date).getFullYear()})
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    {story.personName ? `About ${story.personName} • ` : ''}Submitted by {story.submittedBy?.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>{story.description}</Typography>
                  {story.details && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{story.details}</Typography>}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" color="success"
                      onClick={async () => {
                        try { const r = await reviewStory(story._id, 'APPROVE'); flash(r.message); await load(); }
                        catch (err) { setError(err.response?.data?.error || 'Could not approve.'); }
                      }}>
                      Approve
                    </Button>
                    <Button size="small" variant="outlined" color="error"
                      onClick={async () => {
                        try { const r = await reviewStory(story._id, 'REJECT'); flash(r.message); await load(); }
                        catch (err) { setError(err.response?.data?.error || 'Could not reject.'); }
                      }}>
                      Reject
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </CardContent>
      </Card>

      {/* Pending name corrections */}
      <Card sx={{ borderRadius: 3, mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Pending name corrections {pendingNames.length > 0 && `(${pendingNames.length})`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Members can suggest a name correction for themselves, their spouse, or a
            direct ancestor. Nothing changes in the tree until you approve it here.
          </Typography>
          {pendingNames.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No corrections waiting for review.</Typography>
          ) : (
            pendingNames.map((change) => (
              <Card key={change._id} variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
                <CardContent sx={{ pb: '12px !important' }}>
                  <Typography variant="body2">
                    <s style={{ opacity: 0.6 }}>{change.currentName}</s> → <b>{change.proposedName}</b>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Suggested by {change.submittedBy?.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" color="success"
                      onClick={async () => {
                        try { const r = await reviewPersonChange(change._id, 'APPROVE'); flash(r.message); await load(); }
                        catch (err) { setError(err.response?.data?.error || 'Could not approve.'); }
                      }}>
                      Approve
                    </Button>
                    <Button size="small" variant="outlined" color="error"
                      onClick={async () => {
                        try { const r = await reviewPersonChange(change._id, 'REJECT'); flash(r.message); await load(); }
                        catch (err) { setError(err.response?.data?.error || 'Could not reject.'); }
                      }}>
                      Reject
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </CardContent>
      </Card>

      {/* Pending spouse additions (3rd+ wife) */}
      <Card sx={{ borderRadius: 3, mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Pending spouse additions {pendingSpouses.length > 0 && `(${pendingSpouses.length})`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Per family policy, a man's 3rd wife and beyond need your review after consulting
            the family — to guard against the tree being used to add unrecognized partners.
          </Typography>
          {pendingSpouses.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No spouse additions waiting for review.</Typography>
          ) : (
            pendingSpouses.map((p) => (
              <Card key={p._id} variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
                <CardContent sx={{ pb: '12px !important' }}>
                  <Typography variant="body2">
                    Add <b>{p.proposedWife?.name}</b> as a wife of <b>{p.husband?.name}</b>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {p.husband?.name} already has {p.existingWifeCount} recorded wives — proposed by {p.submittedBy?.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" color="success"
                      onClick={async () => {
                        try { const r = await reviewSpouseAddition(p._id, 'APPROVE'); flash(r.message); await load(); }
                        catch (err) { setError(err.response?.data?.error || 'Could not approve.'); }
                      }}>
                      Approve
                    </Button>
                    <Button size="small" variant="outlined" color="error"
                      onClick={async () => {
                        try { const r = await reviewSpouseAddition(p._id, 'REJECT'); flash(r.message); await load(); }
                        catch (err) { setError(err.response?.data?.error || 'Could not reject.'); }
                      }}>
                      Reject
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </CardContent>
      </Card>


      <Card sx={{ borderRadius: 3, mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Chat groups {chatGroups.length > 0 && `(${chatGroups.length})`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Private group rooms created by members. For privacy, their messages
            are visible only to their members — you can see names and sizes,
            and delete a group if something harmful is reported.
          </Typography>
          {chatGroups.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No groups created yet.</Typography>
          ) : (
            chatGroups.map((g) => (
              <Box key={g.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f0f0f0' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{g.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Created by {g.createdBy} • {g.memberCount} member{g.memberCount === 1 ? '' : 's'}
                  </Typography>
                </Box>
                <Button size="small" color="error"
                  onClick={async () => {
                    if (!window.confirm(`Delete the group "${g.name}" and all its messages?`)) return;
                    try { const r = await deleteGroup(g.id); flash(r.message); await load(); }
                    catch (err) { setError(err.response?.data?.error || 'Could not delete the group.'); }
                  }}>
                  Delete
                </Button>
              </Box>
            ))
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(toDelete)} onClose={() => setToDelete(null)}>
        <DialogTitle>Delete this account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The account of <b>{toDelete?.name}</b> will be deleted and their tree node
            released for claiming again. The person remains in the family tree.
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)}>Cancel</Button>
          <Button color="error" onClick={confirmDelete}>Delete account</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/**
 * Admin-assisted registration: for members who can't get through the
 * self-service flow (SMS issues, unfamiliar with phones, etc). The admin
 * finds their tree node, verifies who they are directly, sets a phone
 * and starting password, and relays the credentials to the member —
 * who is expected to change the password themselves afterward.
 */
const randomPassword = () => {
  // Avoids visually-ambiguous characters (0/O, 1/l/I) since this often
  // gets read aloud or copied onto paper for someone else to type in.
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let out = '';
  for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
};

const AssistRegistrationCard = ({ onCreated }) => {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [createdInfo, setCreatedInfo] = useState(null); // { name, phone, password, message }

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        setResults(await adminSearchPeople(query.trim()));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const reset = () => {
    setSelected(null);
    setQuery('');
    setResults([]);
    setPhone('');
    setEmail('');
    setPassword('');
    setError('');
  };

  const handleCreate = async () => {
    setError('');
    if (!selected) { setError('Pick a person from the tree first.'); return; }
    if (!phone.trim()) { setError('Enter their phone number.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setBusy(true);
    try {
      const data = await adminCreateAccount({
        personId: selected.id, phone: phone.trim(), email: email.trim() || undefined, password,
      });
      setCreatedInfo({ name: selected.name, phone: data.user.phone, password, message: data.message });
      reset();
      setExpanded(false);
      await onCreated?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create the account.');
    } finally {
      setBusy(false);
    }
  };

  const credentialsText = createdInfo
    ? `Mbaa Mwendwa login for ${createdInfo.name}\nPhone: ${createdInfo.phone}\nPassword: ${createdInfo.password}\n(Please change your password in Settings after logging in.)`
    : '';

  return (
    <Card sx={{ borderRadius: 3, mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => setExpanded((v) => !v)}>
          <PersonAddIcon color="action" />
          <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Set up an account for a member</Typography>
          <Button size="small">{expanded ? 'Close' : 'Open'}</Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          For members struggling to register themselves. Verify who they are directly
          (in person or by phone call), then set up their login here — no code needed.
          Give them the phone number and password, and ask them to change the password
          in Settings once they log in.
        </Typography>

        {createdInfo && (
          <Alert severity="success" sx={{ mt: 2 }}
            action={
              <IconButton size="small" onClick={() => navigator.clipboard?.writeText(credentialsText)} aria-label="Copy credentials">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            }>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{createdInfo.message}</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Phone: <b>{createdInfo.phone}</b> &nbsp; Password: <b>{createdInfo.password}</b>
            </Typography>
          </Alert>
        )}

        {expanded && (
          <Box sx={{ mt: 2 }}>
            {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

            {!selected ? (
              <>
                <TextField
                  label="Search the tree by name"
                  fullWidth
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  InputProps={{
                    endAdornment: searching ? <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment> : null,
                  }}
                />
                {results.length > 0 && (
                  <List dense sx={{ mt: 1, maxHeight: 260, overflowY: 'auto', border: '1px solid #eee', borderRadius: 2 }}>
                    {results.map((p) => (
                      <ListItemButton key={p.id} disabled={p.claimed}
                        onClick={() => setSelected(p)}>
                        <ListItemText
                          primary={p.name}
                          secondary={
                            p.claimed
                              ? `Already registered (${p.claimedPhone})`
                              : [p.motherName && `Mother: ${p.motherName}`, p.fatherName && `Father: ${p.fatherName}`].filter(Boolean).join(' • ') || 'No parents recorded'
                          }
                        />
                        {p.claimed && <Chip label="Registered" size="small" />}
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Setting up: <b>{selected.name}</b>{' '}
                  <Button size="small" onClick={() => setSelected(null)}>Change</Button>
                </Typography>
                <TextField label="Phone number" fullWidth margin="dense"
                  placeholder="e.g. 0712345678" value={phone} onChange={(e) => setPhone(e.target.value)} />
                <TextField label="Email (optional)" type="email" fullWidth margin="dense"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  helperText="Recommended — makes it easier for them to recover access later" />
                <TextField label="Starting password" fullWidth margin="dense"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  helperText="At least 8 characters. They should change this after logging in."
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Generate a random password">
                          <IconButton onClick={() => setPassword(randomPassword())} aria-label="Generate password">
                            <CasinoIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }} />
                <Button variant="contained" sx={{ mt: 1.5 }} disabled={busy} onClick={handleCreate}>
                  {busy ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Create their account'}
                </Button>
              </>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminPage;
